import { useState, useContext } from "react";
import { useNavigate } from "react-router";
import {
  Button,
  Card,
  List,
  Modal,
  Input,
  Typography,
  Space,
  Popconfirm,
  Empty,
  Row,
  Col,
  Spin,
} from "antd";
import {
  PlusOutlined,
  DeleteOutlined,
  FolderOpenOutlined,
} from "@ant-design/icons";
import { FirebaseContext } from "../context/FirebaseContext";
import useProjects from "../hooks/useProjects";

const { Title, Text } = Typography;
const { TextArea } = Input;

interface ProjectManagerProps {}

const ProjectManager: React.FC<ProjectManagerProps> = () => {
  const firebase = useContext(FirebaseContext);
  const navigate = useNavigate();
  const user = firebase.auth.currentUser;

  const {
    projects,
    loading,
    creating,
    deleting,
    createProject,
    deleteProject,
  } = useProjects(user);

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [newProjectName, setNewProjectName] = useState("");
  const [newProjectDescription, setNewProjectDescription] = useState("");

  const handleCreateProject = async () => {
    if (newProjectName.trim()) {
      const project = await createProject({
        name: newProjectName.trim(),
        description: newProjectDescription.trim(),
      });

      if (project) {
        setNewProjectName("");
        setNewProjectDescription("");
        setIsModalVisible(false);
      }
    }
  };

  const handleDeleteProject = async (projectId: string) => {
    await deleteProject(projectId);
  };

  const handleSelectProject = (projectId: string) => {
    navigate(`/project/${projectId}`);
  };

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "50vh",
        }}
      >
        <Spin size="large" tip="Cargando proyectos..." />
      </div>
    );
  }

  return (
    <div style={{ padding: "24px" }}>
      <div style={{ marginBottom: "24px" }}>
        <Row align="middle" justify="space-between">
          <Col>
            <Title level={3}>Gestión de Proyectos</Title>
            <Text type="secondary">
              Crea, organiza y gestiona tus proyectos de análisis de imágenes
            </Text>
          </Col>
          <Col>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setIsModalVisible(true)}
              loading={creating}
            >
              Nuevo Proyecto
            </Button>
          </Col>
        </Row>
      </div>

      {projects.length === 0 ? (
        <Empty
          description="No hay proyectos creados"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        >
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setIsModalVisible(true)}
            loading={creating}
          >
            Crear primer proyecto
          </Button>
        </Empty>
      ) : (
        <List
          grid={{ gutter: 16, xs: 1, sm: 2, md: 2, lg: 3, xl: 3, xxl: 4 }}
          dataSource={projects}
          renderItem={(project) => (
            <List.Item>
              <Card
                hoverable
                onClick={() => handleSelectProject(project.id)}
                style={{ cursor: "pointer" }}
                loading={deleting === project.id}
                actions={[
                  <Popconfirm
                    title="¿Eliminar proyecto?"
                    description="Esta acción no se puede deshacer"
                    onConfirm={(e) => {
                      e?.stopPropagation();
                      handleDeleteProject(project.id);
                    }}
                    onCancel={(e) => e?.stopPropagation()}
                    okText="Eliminar"
                    cancelText="Cancelar"
                    okType="danger"
                  >
                    <Button
                      type="text"
                      icon={<DeleteOutlined />}
                      danger
                      loading={deleting === project.id}
                      onClick={(e) => e.stopPropagation()}
                    >
                      Eliminar
                    </Button>
                  </Popconfirm>,
                ]}
              >
                <Card.Meta
                  avatar={
                    <FolderOpenOutlined
                      style={{ fontSize: "24px", color: "#1890ff" }}
                    />
                  }
                  title={
                    <Space>
                      <span>{project.name}</span>
                      <Text type="secondary" style={{ fontSize: "12px" }}>
                        (Click para abrir)
                      </Text>
                    </Space>
                  }
                  description={
                    <Space
                      direction="vertical"
                      size="small"
                      style={{ width: "100%" }}
                    >
                      <Text type="secondary">{project.description}</Text>
                      <Text type="secondary" style={{ fontSize: "12px" }}>
                        Modificado: {project.lastModified.toLocaleDateString()}
                      </Text>
                    </Space>
                  }
                />
              </Card>
            </List.Item>
          )}
        />
      )}

      <Modal
        title="Crear Nuevo Proyecto"
        open={isModalVisible}
        onOk={handleCreateProject}
        onCancel={() => {
          setIsModalVisible(false);
          setNewProjectName("");
          setNewProjectDescription("");
        }}
        okText="Crear"
        cancelText="Cancelar"
        confirmLoading={creating}
        okButtonProps={{ disabled: !newProjectName.trim() }}
      >
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          <div>
            <Text strong>Nombre del proyecto *</Text>
            <Input
              placeholder="Ingresa el nombre del proyecto"
              value={newProjectName}
              onChange={(e) => setNewProjectName(e.target.value)}
              style={{ marginTop: "8px" }}
            />
          </div>
          <div>
            <Text strong>Descripción</Text>
            <TextArea
              placeholder="Describe el objetivo de este proyecto"
              value={newProjectDescription}
              onChange={(e) => setNewProjectDescription(e.target.value)}
              rows={3}
              style={{ marginTop: "8px" }}
            />
          </div>
        </Space>
      </Modal>
    </div>
  );
};

export default ProjectManager;
